import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Paper, Table, TableHead, TableBody, TableRow, TableCell,
  Button, TextField, IconButton, Typography, Chip, Dialog,
  DialogTitle, DialogContent, DialogActions
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from "@material-ui/icons/Add";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  mainPaper: { flex: 1, padding: theme.spacing(1), overflowY: "scroll", ...theme.scrollbarStyles },
  colorSwatch: { width: 24, height: 24, borderRadius: 4, display: "inline-block", border: `1px solid ${theme.palette.divider}` },
}));

const PRESET_COLORS = ["#e74c3c","#e67e22","#f1c40f","#2ecc71","#1abc9c","#3498db","#9b59b6","#34495e","#6c757d","#e91e63"];

const TAG_COLOR_PALETTE = [
  "#ef4444", "#f97316", "#eab308", "#22c55e", "#10b981",
  "#06b6d4", "#3b82f6", "#8b5cf6", "#ec4899", "#6b7280"
];

const getNextAvailableColor = (existingTags) => {
  const usedColors = new Set((existingTags || []).map(t => (t.color || "").toLowerCase()));
  const next = TAG_COLOR_PALETTE.find(c => !usedColors.has(c.toLowerCase()));
  return next || TAG_COLOR_PALETTE[0];
};

const Tags = () => {
  const classes = useStyles();
  const [tags, setTags] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ name: "", color: "#3b82f6" });
  const [loading, setLoading] = useState(false);

  const fetchTags = async () => {
    try {
      const { data } = await api.get("/tags");
      setTags(data);
    } catch (err) { toastError(err); }
  };

  useEffect(() => { fetchTags(); }, []);

  const handleCreate = async () => {
    if (!form.name.trim()) return;
    setLoading(true);
    try {
      await api.post("/tags", { name: form.name, color: form.color });
      toast.success(i18n.t("tags.creada"));
      setForm({ name: "", color: "#3b82f6" });
      setDialogOpen(false);
      fetchTags();
    } catch (err) { toastError(err); }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/tags/${id}`);
      toast.success(i18n.t("tags.eliminada"));
      fetchTags();
    } catch (err) { toastError(err); }
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>{i18n.t("tags.titulo")}</Title>
        <MainHeaderButtonsWrapper>
          <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => {
            const autoColor = getNextAvailableColor(tags);
            setForm({ name: "", color: autoColor });
            setDialogOpen(true);
          }}>
            {i18n.t("tags.nueva")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{i18n.t("tags.headerColor")}</TableCell>
              <TableCell>{i18n.t("tags.headerNombre")}</TableCell>
              <TableCell align="center">{i18n.t("tags.headerAcciones")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tags.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  <Typography variant="body2" color="textSecondary">{i18n.t("tags.sinEtiquetas")}</Typography>
                </TableCell>
              </TableRow>
            )}
            {tags.map(tag => (
              <TableRow key={tag.id}>
                <TableCell>
                  <span className={classes.colorSwatch} style={{ backgroundColor: tag.color }} />
                </TableCell>
                <TableCell>
                  <Chip label={tag.name} size="small" style={{ backgroundColor: tag.color, color: "#fff" }} />
                </TableCell>
                <TableCell align="center">
                  <IconButton size="small" onClick={() => handleDelete(tag.id)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{i18n.t("tags.nueva")}</DialogTitle>
        <DialogContent>
          <TextField
            label={i18n.t("tags.nombre")}
            fullWidth
            size="small"
            variant="outlined"
            value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })}
            style={{ marginBottom: 16 }}
            autoFocus
          />
          <Typography variant="caption" gutterBottom>{i18n.t("tags.color")}</Typography>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 4 }}>
            {PRESET_COLORS.map(c => (
              <span
                key={c}
                onClick={() => setForm({ ...form, color: c })}
                style={{
                  width: 28, height: 28, borderRadius: 4, backgroundColor: c, cursor: "pointer",
                  border: form.color === c ? "3px solid #000" : "1px solid #ccc",
                  boxSizing: "border-box"
                }}
              />
            ))}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12 }}>
            <input type="color" value={form.color} onChange={e => setForm({ ...form, color: e.target.value })} style={{ width: 40, height: 32, cursor: "pointer" }} />
            <Typography variant="caption">{form.color}</Typography>
            <Chip label={form.name || i18n.t("tags.vistaPrevia")} size="small" style={{ backgroundColor: form.color, color: "#fff" }} />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>{i18n.t("tags.cancelar")}</Button>
          <Button variant="contained" color="primary" onClick={handleCreate} disabled={!form.name.trim() || loading}>
            {i18n.t("tags.crear")}
          </Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default Tags;
